import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import { InputSwitchChangeEvent } from "primereact/inputswitch";
import { PageProps } from "../../../models/routing.model";
import { useTheme } from "../../../contexts/Theme.context";
import {
	CallCenterPreferences,
	DEFAULT_AUDIO_DEVICE_ID,
	getCallCenterPreferences,
	persistCallCenterPreferences,
} from "../../../services/call-center/call-center-preferences.service";
import TwilioVoiceService from "../../../services/twilio/twilio-voice.service";
import UiCard from "../../../components/card/Card";
import UiInputSwitch from "../../../components/input-switch/UiInputSwitch.component";

const isOutputSelectionSupported =
	typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

const SettingsPreferences = (props: PageProps) => {
	const { t } = useTranslation("common");

	// Theme
	const { theme, setTheme } = useTheme();

	// Call center
	const [preferences, setPreferences] = useState<CallCenterPreferences>(() =>
		getCallCenterPreferences()
	);
	const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
	const [permissionMissing, setPermissionMissing] = useState(false);

	useEffect(() => {
		let mounted = true;

		const loadDevices = async () => {
			try {
				let list = await navigator.mediaDevices.enumerateDevices();
				// Without mic permission the browser hides device labels — ask once,
				// then release the stream immediately.
				if (!list.some((device) => device.label)) {
					const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
					stream.getTracks().forEach((track) => track.stop());
					list = await navigator.mediaDevices.enumerateDevices();
				}
				if (mounted) {
					setDevices(list);
					setPermissionMissing(false);
				}
			} catch {
				if (mounted) setPermissionMissing(true);
			}
		};

		loadDevices();
		navigator.mediaDevices?.addEventListener("devicechange", loadDevices);
		return () => {
			mounted = false;
			navigator.mediaDevices?.removeEventListener("devicechange", loadDevices);
		};
	}, []);

	const update = (partial: Partial<CallCenterPreferences>) => {
		const next = { ...preferences, ...partial };
		setPreferences(next);
		persistCallCenterPreferences(next);
		// Push onto the live Twilio Device (no-op when not initialized yet)
		TwilioVoiceService.applyAudioPreferences();
	};

	const deviceOptions = (kind: MediaDeviceKind, fallbackLabelKey: string) => [
		{ id: DEFAULT_AUDIO_DEVICE_ID, label: t("SETTINGS.CALL_CENTER.DEFAULT_DEVICE") },
		...devices
			.filter(
				(device) =>
					device.kind === kind &&
					device.deviceId &&
					device.deviceId !== DEFAULT_AUDIO_DEVICE_ID
			)
			.map((device, index) => ({
				id: device.deviceId,
				label: device.label || `${t(fallbackLabelKey)} ${index + 1}`,
			})),
	];

	const microphoneOptions = deviceOptions("audioinput", "SETTINGS.CALL_CENTER.MICROPHONE");
	const speakerOptions = deviceOptions("audiooutput", "SETTINGS.CALL_CENTER.SPEAKER");

	return (
		<div
			id={props.id}
			className="settings-preferences"
		>
			{/* Theme */}
			<UiCard>
				<div className="settings-theme">
					<h2 className="settings-theme__title">{t("SETTINGS.THEME.TITLE")}</h2>
					<p className="settings-theme__desc">{t("SETTINGS.THEME.DESCRIPTION")}</p>

					<div className="settings-theme__row">
						<UiInputSwitch
							id="darkModeSwitch"
							name="darkModeSwitch"
							checked={theme === "dark"}
							onChange={(event: InputSwitchChangeEvent) =>
								setTheme(event.value ? "dark" : "light")
							}
							label="SETTINGS.THEME.DARK_MODE"
							removeBottomSpacer
						/>
					</div>
				</div>
			</UiCard>

			{/* Call center */}
			<UiCard>
				<div className="settings-call-center">
					<h2 className="settings-call-center__title">
						{t("SETTINGS.CALL_CENTER.TITLE")}
					</h2>
					<p className="settings-call-center__desc">
						{t("SETTINGS.CALL_CENTER.DESCRIPTION")}
					</p>

					{/* Ring sound */}
					<div className="settings-call-center__row">
						<UiInputSwitch
							id="ringSoundSwitch"
							name="ringSoundSwitch"
							checked={preferences.ringSoundEnabled}
							onChange={(event: InputSwitchChangeEvent) =>
								update({ ringSoundEnabled: !!event.value })
							}
							label="SETTINGS.CALL_CENTER.RING_SOUND"
							removeBottomSpacer
						/>
					</div>

					{/* Auto-open call bar */}
					<div className="settings-call-center__row">
						<UiInputSwitch
							id="autoOpenCallBarSwitch"
							name="autoOpenCallBarSwitch"
							checked={preferences.autoOpenCallBar}
							onChange={(event: InputSwitchChangeEvent) =>
								update({ autoOpenCallBar: !!event.value })
							}
							label="SETTINGS.CALL_CENTER.AUTO_OPEN_CALL_BAR"
							removeBottomSpacer
						/>
					</div>

					<h3 className="settings-call-center__subtitle">
						{t("SETTINGS.CALL_CENTER.AUDIO_DEVICES")}
					</h3>
					{permissionMissing && (
						<p className="settings-call-center__hint">
							{t("SETTINGS.CALL_CENTER.PERMISSION_HINT")}
						</p>
					)}

					{/* Microphone */}
					<div className="settings-call-center__field">
						<label htmlFor="inputDevice">{t("SETTINGS.CALL_CENTER.MICROPHONE")}</label>
						<Dropdown
							id="inputDevice"
							name="inputDevice"
							value={preferences.inputDeviceId}
							options={microphoneOptions}
							optionValue="id"
							optionLabel="label"
							onChange={(event) => update({ inputDeviceId: event.value })}
						/>
					</div>

					{/* Speaker */}
					<div className="settings-call-center__field">
						<label htmlFor="outputDevice">{t("SETTINGS.CALL_CENTER.SPEAKER")}</label>
						<Dropdown
							id="outputDevice"
							name="outputDevice"
							value={preferences.outputDeviceId}
							options={speakerOptions}
							optionValue="id"
							optionLabel="label"
							disabled={!isOutputSelectionSupported}
							onChange={(event) => update({ outputDeviceId: event.value })}
						/>
						{!isOutputSelectionSupported && (
							<small>{t("SETTINGS.CALL_CENTER.OUTPUT_NOT_SUPPORTED")}</small>
						)}
					</div>
				</div>
			</UiCard>
		</div>
	);
};

export default SettingsPreferences;
